<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop,
            'q-mb-lg': $q.platform.is.desktop
        }"
    >
        <q-form @submit="handleSubmit" ref="eventForm">
            <!-- status dropdown -->
            <q-input
                v-model="form.status"
                :label="$t('Status')"
                outlined
                :disable="true"
                class="q-mb-md bg-white"
            />
            <!-- asset dropdown -->
            <q-select
                v-model="form.asset"
                :options="assetOptions"
                :label="$t('Asset')"
                :rules="[
                    val => (val !== '' && val !== null) || $t('Select an asset')
                ]"
                class="q-mb-md"
                behavior="dialog"
                clearable
                outlined
                emit-value
                map-options
                disable
                bg-color="white"
                color="accent"
            />
            <!-- Product select -->
            <table-select
                v-model="form.product"
                @confirmSelection="form.product = productSelectProps.selection"
                :baseURL="productSelectProps.url"
                :queryParams="productSelectProps.queryParams"
                :label="productSelectProps.label"
                :dialogTitle="productSelectProps.dialogTitle"
                :searchLabel="productSelectProps.searchLabel"
                :disable="true"
                row-key="id"
                class="q-mb-md"
            >
                <template v-slot:item="data">
                    <product-card
                        @select="
                            productSelectProps.selection = {
                                label: data.props.row.name,
                                value: data.props.row.product
                            }
                        "
                        :name="data.props.row.name"
                        :code="data.props.row.code"
                        :description="data.props.row.description"
                        :unit-measure="data.props.row.unit_measure"
                        :unit-packing="data.props.row.unit_packing"
                        :isSelected="
                            productSelectProps.selection &&
                            productSelectProps.selection.value ===
                                data.props.row.product
                        "
                        :isSelectable="true"
                    ></product-card>
                </template>
            </table-select>

            <!-- start datetime picker -->
            <div>
                <date-time-picker
                    v-model="form.start"
                    :disable="true"
                    :label="$t('Start')"
                    :rules="[val => val !== '' || $t('Pick a date and time')]"
                    :error-message="dateRangeErrorMessage"
                />
                <q-tooltip>
                    {{
                        $t(
                            'Go to the asset timeline page to edit the time of this event.'
                        )
                    }}
                </q-tooltip>
            </div>
            <!-- end datetime picker -->
            <div>
                <date-time-picker
                    v-model="form.end"
                    :disable="true"
                    :label="$t('End')"
                    :error-message="dateRangeErrorMessage"
                />
                <q-tooltip>
                    {{
                        $t(
                            'Go to the asset timeline page to edit the time of this event.'
                        )
                    }}
                </q-tooltip>
            </div>

            <!-- Defect select -->
            <defect-table-select
                id="detail-defect-select"
                v-if="defectSelectProps.url"
                v-model="form.defect"
                @confirmSelection="
                    form.defect = {
                        label: defectSelectProps.selection.name,
                        value: defectSelectProps.selection.id
                    }
                "
                :baseURL="defectSelectProps.url"
                :queryParams="defectSelectProps.queryParams"
                :label="defectSelectProps.label"
                :dialogTitle="defectSelectProps.dialogTitle"
                :searchLabel="defectSelectProps.searchLabel"
                :facilityId="defectSelectProps.facilityId"
                row-key="id"
                class="col-lg-3 col-md-6 col-sm-6 col-xs-12 q-mb-md"
            >
                <template v-slot:item="data">
                    <defect-card
                        @select="defectSelectProps.selection = data.props.row"
                        :name="data.props.row.name"
                        :code="data.props.row.code"
                        :description="data.props.row.description"
                        :isSelected="
                            defectSelectProps.selection &&
                            defectSelectProps.selection.id === data.props.row.id
                        "
                        :isSelectable="true"
                    />
                </template>
            </defect-table-select>

            <!-- rejected production input -->
            <q-input
                v-model="form.rejectedProductCount"
                data-test="description-textarea"
                :label="$t('Rejected Production')"
                class="q-mb-lg bg-white"
                outlined
                :disable="true"
            />

            <!-- total production input -->
            <q-input
                v-model="form.grossProductCount"
                data-test="description-textarea"
                :label="$t('Total Products')"
                class="q-mb-lg bg-white"
                outlined
                :disable="true"
            />

            <!-- action buttons -->
            <div class="form__actions">
                <q-btn
                    v-show="form.asset"
                    @click="
                        $router.push({
                            name: 'asset_timeline',
                            params: {
                                assetId: form.asset?.value || null,
                                facilityId: facilityId
                            }
                        })
                    "
                    :label="$t('See asset timeline')"
                    icon="fas fa-history"
                    class="q-mb-md q-px-sm bg-white"
                    color="primary"
                    no-caps
                    outline
                >
                </q-btn>

                <!-- 'Save' button -->
                <q-btn
                    :label="$t('Save')"
                    :disable="$userCannot('edit', 'phases') || !formHasChanged"
                    class="float-right"
                    :loading="isLoading"
                    type="submit"
                    color="primary"
                    id="save-button"
                />
                <!-- 'Cancel' button -->
                <q-btn
                    @click="closeDetail"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm bg-white"
                    color="white"
                    text-color="primary"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import DateTimePicker from '@components/DateTimePicker.vue';
import DefectCard from '@components/DefectCard.vue';
import DefectTableSelect from '@components/DefectTableSelect.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { mapState, mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFacilitiesStore } from '@stores/facilities';
import { useAssetsStore } from '@stores/assets';

export default {
    name: 'QualityEventsDetail',
    components: {
        ProductCard,
        DateTimePicker,
        TableSelect,
        DefectCard,
        DefectTableSelect
    },
    mixins: [BreadcrumbsManager],
    emits: ['save', 'return'],
    props: {
        facilityId: {
            type: Number,
            required: true
        },
        eventId: {
            type: Number,
            required: false,
            default: null
        },
        productId: {
            type: Number,
            required: false,
            default: null
        },
        productName: {
            type: String,
            required: false,
            default: null
        },
        assetId: {
            type: Number,
            required: false,
            default: null
        },
        assetName: {
            type: String,
            required: false,
            default: null
        },
        start: {
            type: String,
            required: false,
            default: null
        },
        end: {
            type: [String, null],
            required: false,
            default: null
        },
        defectId: {
            type: Number,
            required: false,
            default: null
        },
        defectName: {
            type: String,
            required: false,
            default: null
        },
        rejectedProductCount: {
            type: Number,
            required: false,
            default: null
        },
        grossProductCount: {
            type: Number,
            required: false,
            default: null
        },
        editEvent: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    async created() {
        // begin loading
        this.beginLoading(`${this.$t('Loading event')}...`);
        // init form on update mode
        if (this.isEdit) {
            await this.initForm();
        } else {
            // init time range in the datetime picker in create mode
            this.initDateTimeRange();
        }

        // init the options for asset dropdown
        this.initAssetOptions();

        // stop loading
        this.stopLoading();
    },
    data() {
        return {
            form: {
                facilityId: null,
                status: null,
                eventId: null,
                asset: null,
                product: null,
                start: null,
                end: null,
                defect: null,
                rejectedProductCount: null,
                grossProductCount: null
            },
            isLoading: false,
            dateRangeErrorMessage: '',
            assetOptions: [],
            disableProduct: true,
            disableCause: true,

            // remote selectors needs to be remounted when asset changes, as the cause/product values
            // are reset to null, causing the form validators to raise errors
            remoteSelectKey: 0,

            // selectors
            defectSelectProps: {
                url: `assets/${this.assetId}/defects/`,
                queryParams: { order: 'name' },
                label: this.$t('Defect'),
                dialogTitle: this.$t('Select a Defect'),
                searchLabel: this.$t('Search a defect'),
                selection: null,
                facilityId: null
            },
            productSelectProps: {
                url: `assets/${this.assetId}/products/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useAssetsStore, ['getAssets']),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Determine if form is in edit mode.
         * @return {boolean}
         */
        isEdit() {
            return this.eventId !== null;
        },
        /**
         * Server formatted payload.
         * @return {Object}
         */
        payload() {
            const payload = {
                defect: null
            };

            if (this.form.defect && this.form.defect.value) {
                payload['defect'] = parseInt(this.form.defect.value);
            }

            return payload;
        },
        /**
         * Enable the submit button when the user updates the form.
         * @return {Boolean}
         */
        formHasChanged() {
            return this.defectId !== this.form.defect?.value;
        }
    },
    watch: {
        'form.asset': {
            immediate: true,
            handler(currId, prevId) {
                // clear the dropdown if the user selects a different asset
                if (prevId && currId !== prevId) {
                    this.form.product = null;
                    this.form.cause = null;
                    // remount remote selectors
                    this.remoteSelectKey++;
                }

                // corner case: the asset id is null or empty
                if (!currId) {
                    // disable the product and cause dropdowns
                    this.disableProduct = true;
                    this.disableCause = true;
                } else {
                    // when the asset id exists enable the dropdowns
                    this.disableProduct = false;
                    this.disableCause = false;
                }
            }
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Init event detail form.
         */
        async initForm() {
            this.form.facilityId = this.facilityId;
            this.form.status = this.$t('Quality');
            this.form.eventId = this.eventId;
            this.form.start = this.start;
            this.form.end = this.end;
            this.form.rejectedProductCount = this.rejectedProductCount;
            this.form.grossProductCount = this.grossProductCount;
            this.form.asset = {
                label: this.assetName,
                value: this.assetId
            };
            this.form.product = {
                label: this.productName,
                value: this.productId
            };
            this.form.defect = {
                label: this.defectName,
                value: this.defectId
            };
        },
        /**
         * Init the options of the asset dropdown
         */
        initAssetOptions() {
            // get the assets from store
            const assets = this.getAssets(this.facilityId);
            // init the asset options
            assets.forEach(asset => {
                this.assetOptions.push({
                    label: asset['name'],
                    value: asset['id']
                });
            });
        },
        /**
         * Handle the behavior when submitting the form
         */
        async handleSubmit() {
            // validate datetime range
            let isValid = true;
            // change the format of datetime to server format
            const start = this.form.start;
            const end = this.form.end;
            if (end && start >= end) {
                this.dateRangeErrorMessage = this.$t(
                    'Start must be before end'
                );
                isValid = false;
            }

            if (isValid) {
                this.isLoading = true;
                this.dateRangeErrorMessage = '';
                await this.update();
                if (this.editEvent) {
                    this.$emit('save');
                    this.closeDetail();
                }
                this.isLoading = false;
            }
        },
        /**
         * Update event.
         */
        async update() {
            const errorMessage = this.$t(
                'Failed to update event. Please try again later.'
            );
            // send PUT http request
            try {
                await this.$axios.put(
                    `quality_events/${this.eventId}/`,
                    this.payload
                );
                this.$notifySuccess(this.$t('Event updated'));
            } catch (error) {
                if (
                    error &&
                    typeof error == 'object' &&
                    'uneditable_phase' in error
                ) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(errorMessage, error);
                }
            }
        },
        closeDetail() {
            if (this.editEvent) {
                this.$emit('return');
            } else {
                this.$router.go(-1);
            }
        },

        /**************
         * Datetime related methods *
         *************/

        /**
         * init the time range in ISO 8601 format
         * i.e. start: '2020-03-03T20:35:39.700Z'; end: ''
         */
        initDateTimeRange() {
            // init start time at now
            this.form['start'] = new Date().toISOString();
            // default end time is empty
            this.form['end'] = '';
        },
        /**
         * Convert a given date to ISO String and reset the milliseconds
         * @return {Date}
         */
        dateToIsoString(date) {
            const newDate = new Date(date);
            newDate.setMilliseconds(0);
            return newDate.toISOString();
        }
    }
};
</script>

<style lang="scss" scoped></style>
